import clsx from "clsx";
import { Theme } from "react-migration/lib/theme/Theme";

type SwitchSize = "small" | "normal" | "xs";
type LabelSide = "left" | "right";

function borderAndBackgrounds(backgroundClass: string, disabled?: boolean, value?: unknown) {
  if (disabled) {
    return `${backgroundClass}/50`;
  }

  if (value) {
    return "bg-platform-600";
  }

  return backgroundClass;
}

export function Switch({
  disabled,
  label,
  id,
  onToggle,
  showLabel,
  value = false,
  size = "normal",
  theme = Theme.Light,
  labelSide = "right",
  backgroundClass = "bg-neutral-200",
  disabledTextContentClass = `text-text-base-primary-disabled-${theme === Theme.Light ? "light" : "dark"}`,
  showLabelClass = "text-xs mr-3",
  ...rest
}: {
  disabled?: boolean;
  label: string;
  id: string;
  onToggle: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showLabel?: boolean;
  value?: boolean;
  size?: SwitchSize;
  theme?: Theme;
  labelSide?: LabelSide;
  backgroundClass?: string;
  disabledTextContentClass?: string;
  showLabelClass?: string;
}) {
  return (
    <div
      className={clsx("switch-wrapper", "flex", "items-center", "justify-center")}
      data-testid={`${id}-switch`}
      {...rest}
    >
      <label
        htmlFor={id}
        className={clsx(
          "flex!",
          "items-center",
          "cursor-pointer",
          disabled ? disabledTextContentClass : "",
          labelSide === "left" ? "flex-row-reverse" : ""
        )}
        onClick={(e) => {
          e.stopPropagation();
        }}
        data-testid="switch-check"
      >
        <div className="relative">
          <input
            data-testid={`${id}-switch-input`}
            type="checkbox"
            id={id}
            onChange={onToggle}
            disabled={disabled}
            checked={value}
            className="absolute w-0 h-px -m-px overflow-hidden"
            aria-checked={value}
          />
          <div
            className={clsx(
              "block",
              // Create 3 cases based on size without using a nested ternary

              {
                "w-7": size === "xs",
                "w-8": size === "small",
                "w-10": size === "normal",

                "h-4": size === "xs",
                "h-5": size === "small",
                "h-6": size === "normal",
              },
              "rounded-full",
              borderAndBackgrounds(backgroundClass, disabled, value)
            )}
          />
          <div
            className={clsx(
              "dot",
              "absolute",
              {
                "left-0.5": size === "small" || size === "xs",
                "left-1": size === "normal",

                "top-0.5": size === "small" || size === "xs",
                "top-1": size === "normal",
              },
              {
                "w-3": size === "xs",
                "w-4": size === "small" || size === "normal",
              },
              {
                "h-3": size === "xs",
                "h-4": size === "small" || size === "normal",
              },
              "rounded-full",
              "transition",
              {
                "translate-x-[0.7rem]": size === "xs" && value,
                "translate-x-3": size === "small" && value,
                "translate-x-4": size === "normal" && value,
              },
              disabled ? "bg-white/50" : "bg-white"
            )}
            data-testid="switch-dot"
          />
        </div>
        <div
          className={showLabel ? showLabelClass : "h-px w-0 -m-px overflow-hidden absolute"}
          data-testid="switch-label"
        >
          {label}
        </div>
      </label>
    </div>
  );
}
